'use client';

import Image from 'next/image';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { CLIENT_EXPORT_SCOPE_TYPE } from '@/app/(main)/client-detail/consts/clientExportScope';
import { EXPORT_FORMAT } from '@/app/(main)/client-detail/consts/exportFormat';
import { isGroupedExportVariant } from '@/app/(main)/client-detail/types/downloadSettingsModalVariant';
import Checkbox from '@/app/components/Checkbox';
import { ModalMobileHeader } from '@/app/components/Modal/ModalMobileHeader';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { cn } from '@/utils/сn';
import { ToggleSwitch } from './components/ToggleSwitch';
import { FILE_FORMAT_OPTIONS } from './consts/fileFormats';
import { getSectionsForVariant } from './consts/sectionsConfig';
import { getDownloadSettingsCopy } from './consts/variantCopy';
import type { DownloadSettingsFormProps, ExportFormat, FormValues, SectionKey, SubItemKey } from './types';
import { buildExportSettingsPayload } from './utils/buildExportSettingsPayload';
import { buildGroupedDownloadPayload } from './utils/buildGroupedDownloadPayload';
import { getInitialFormState } from './utils/formStateFromExportSettings';
import {
  applyIncludeAllToggle,
  applySectionToggle,
  applySubItemToggle,
  clearAllFormValues,
  computeIncludeAll,
  hasAnyExportFieldSelected,
} from './utils/formValueUpdates';

const FETCH_HANDLED_ERROR_STATUSES = [403, 404, 429, 500];

function resolveInitialExportFormat(isAllScope: boolean, savedFormat: ExportFormat): ExportFormat {
  if (!isAllScope) {
    return savedFormat;
  }

  return savedFormat === EXPORT_FORMAT.PDF ? EXPORT_FORMAT.CSV : savedFormat;
}

function isExportFormatDisabled(
  format: ExportFormat,
  isAllScope: boolean,
  includePassportFilesAsSeparatePdfs: boolean
): boolean {
  if (isAllScope && format === EXPORT_FORMAT.PDF) {
    return true;
  }

  return includePassportFilesAsSeparatePdfs && format !== EXPORT_FORMAT.PDF;
}

export const DownloadSettingsForm = ({
  initialExportSettings,
  onClose,
  clientsId,
  variant,
  scopeType,
  scopeFilters,
  saveExportSettings,
  isUpdating,
  groupedDownload,
  isDownloading,
}: DownloadSettingsFormProps) => {
  const isAllScope = scopeType === CLIENT_EXPORT_SCOPE_TYPE.ALL;
  const initial = getInitialFormState(initialExportSettings, variant);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>(() =>
    resolveInitialExportFormat(isAllScope, initial.format)
  );
  const [values, setValues] = useState<FormValues>(() => ({
    ...initial.values,
    ...(isAllScope ? { include_passport_files_as_separate_pdfs: false } : {}),
  }));

  const sections = getSectionsForVariant(variant);
  const copy = getDownloadSettingsCopy(variant);

  const includeAllOptions = isAllScope ? { excludePassport: true as const } : {};
  const includePassportFilesAsSeparatePdfs = isAllScope
    ? false
    : values.include_passport_files_as_separate_pdfs;
  const hasAnySelected = hasAnyExportFieldSelected(values, sections);
  const isFullExportSelection = computeIncludeAll(values, sections, includeAllOptions);

  const handlePassportToggle = () => {
    if (isAllScope) {
      return;
    }

    const next = !values.include_passport_files_as_separate_pdfs;
    setValues((previous) => ({ ...previous, include_passport_files_as_separate_pdfs: next }));
    if (next) {
      setSelectedFormat(EXPORT_FORMAT.PDF);
    }
  };

  const handleIncludeAll = () => {
    const allOn = computeIncludeAll(values, sections, includeAllOptions);
    const updated = applyIncludeAllToggle(values, sections, !allOn, includeAllOptions);
    if (!allOn && !isAllScope) {
      setSelectedFormat(EXPORT_FORMAT.PDF);
    }
    setValues(updated);
  };

  const handleToggle = (key: SectionKey) => {
    setValues((previous) => applySectionToggle(previous, sections, key));
  };

  const handleSubItemToggle = (key: SubItemKey) => {
    setValues((previous) => applySubItemToggle(previous, sections, key));
  };

  const handleClearAll = () => {
    setValues(clearAllFormValues());
    setSelectedFormat(isAllScope ? EXPORT_FORMAT.CSV : EXPORT_FORMAT.PDF);
  };

  const handleSaveSelection = () => {
    const settings = buildExportSettingsPayload(values, selectedFormat, variant);
    saveExportSettings(
      isGroupedExportVariant(variant) ? { settings, save_scope: 'group_view' } : { settings, save_scope: 'default' },
      { onSuccess: onClose }
    );
  };

  const handleExportData = () => {
    groupedDownload(
      buildGroupedDownloadPayload({
        clientsId,
        selectedFormat,
        values,
        variant,
        scopeType,
        scopeFilters,
      }),
      {
        onSuccess: onClose,
        onError: (error) => {
          if (FETCH_HANDLED_ERROR_STATUSES.includes(error.status)) return;
          toast.error(error.message || 'Could not generate the export. Please try again.');
        },
      }
    );
  };

  return (
    <div className="flex flex-col overflow-hidden max-md:h-full max-md:w-full md:w-155.5 md:max-h-[85vh]">
      <div className="flex-shrink-0 pt-8 px-4 pb-7.5 md:pt-11.5 md:px-9 md:pb-7">
        <div className="mb-6 md:mb-8">
          <ModalMobileHeader title={copy.title} onBack={onClose} />
        </div>

        <div className="flex flex-col gap-3 w-full">
          <div className="flex gap-3.5 w-full max-md:gap-2">
            {FILE_FORMAT_OPTIONS.map(({ format, label, iconActive, iconInactive }) => {
              const isActive = selectedFormat === format;
              const isDisabled = isExportFormatDisabled(format, isAllScope, includePassportFilesAsSeparatePdfs);
              const showColoredIcon = !isDisabled;

              return (
                <div
                  key={format}
                  onClick={() => !isDisabled && setSelectedFormat(format)}
                  className={cn(
                    'flex-1 min-w-0 flex items-center justify-center gap-2 px-5 py-5 rounded-lg transition-all',
                    'max-md:flex-col max-md:px-5 max-md:py-3',
                    isActive && !isDisabled
                      ? 'bg-blue-50 outline outline-blue-300 shadow-card'
                      : 'outline outline-grey-300',
                    isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  )}
                >
                  <div className="relative w-8 h-8 flex-shrink-0">
                    <Image
                      src={showColoredIcon ? iconActive : iconInactive}
                      alt={label}
                      width={32}
                      height={32}
                      className={cn(isDisabled && 'grayscale opacity-50')}
                    />
                  </div>
                  <span
                    className={cn(
                      'text-body-medium whitespace-nowrap max-md:text-sm max-md:leading-normal',
                      isDisabled ? 'text-grey-600' : 'text-black'
                    )}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
          {isAllScope ? (
            <p className="text-body-small text-grey-700 text-center">
              PDF export is not available when exporting all clients. Please use CSV or Spreadsheet.
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 md:px-9">
        <div className="flex flex-col gap-5 pb-7">
          <div className="flex items-center justify-between">
            <span className="text-button-semibold text-grey-700 max-md:text-sm max-md:leading-normal max-md:text-black">
              {copy.sectionsHeading}
            </span>
            <button
              type="button"
              onClick={isFullExportSelection ? handleClearAll : handleIncludeAll}
              className="text-button-medium text-blue-300 px-1.5 py-1 cursor-pointer max-md:text-sm max-md:leading-[140%]"
            >
              {isFullExportSelection ? 'Clear Selection' : 'Include All'}
            </button>
          </div>

          <div className={cn('flex items-start gap-6', isAllScope && 'opacity-50')}>
            <div className="flex-1">
              <p className="text-button-semibold text-black max-md:text-sm max-md:leading-normal">
                {copy.passportFiles.label}
              </p>
              <p className="text-body-small text-grey-700 mt-0.5">{copy.passportFiles.description}</p>
            </div>
            <ToggleSwitch
              checked={includePassportFilesAsSeparatePdfs}
              onChange={handlePassportToggle}
              disabled={isAllScope}
            />
          </div>

          {sections.map((section, index) => {
            const isOn = values[section.key];
            const hasSubItems = section.subItems && section.subItems.length > 0;
            const isLastSection = index === sections.length - 1;

            return (
              <div key={section.key} className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <span className="flex-1 text-button-semibold text-black max-md:text-sm max-md:leading-normal">
                    {section.label}
                  </span>
                  <ToggleSwitch checked={isOn} onChange={() => handleToggle(section.key)} />
                </div>
                {isOn && hasSubItems && (
                  <div
                    className={cn(
                      'pb-4 border-b border-grey-300',
                      section.columns === 2 ? 'grid grid-cols-2 gap-x-4 gap-y-0' : 'flex flex-col',
                      'max-md:gap-y-4',
                      isLastSection && 'max-md:border-b-0'
                    )}
                  >
                    {section.subItems?.map((subItem) => (
                      <div
                        key={subItem.key}
                        className="flex items-center gap-3 py-2 cursor-pointer max-md:py-0"
                        onClick={() => handleSubItemToggle(subItem.key)}
                      >
                        <Checkbox
                          checked={values[subItem.key]}
                          variant="secondary"
                          size="large"
                          readOnly
                          className="pointer-events-none"
                        />
                        <span className="text-button-medium text-grey-700 max-md:text-sm max-md:leading-normal">
                          {subItem.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex-shrink-0 px-4 pt-4 pb-6 md:px-9 md:pt-5 md:pb-11.5 flex items-center justify-center gap-3 md:gap-5">
        <BaseButton
          type="button"
          size="full"
          intent="ghost_semibold"
          onClick={onClose}
          className="max-w-40 max-md:hidden"
        >
          Cancel
        </BaseButton>
        <BaseButton
          type="button"
          intent="secondary"
          size="full"
          onClick={handleSaveSelection}
          disabled={!hasAnySelected || isUpdating}
          className="max-w-43.75 max-md:max-w-none max-md:flex-1"
        >
          {isUpdating ? 'Saving...' : 'Save Selection'}
        </BaseButton>
        <BaseButton
          size="full"
          type="button"
          onClick={handleExportData}
          disabled={!hasAnySelected || isUpdating || isDownloading}
          className="max-w-43.75 max-md:max-w-none max-md:flex-1"
        >
          {isDownloading ? 'Exporting...' : 'Export Data'}
        </BaseButton>
      </div>
    </div>
  );
};
